import type { VisualizationEvent } from '../../types/simulationPlayback';
import {
  FoupOwnershipLedger,
  seedPortInventoryFromFirstEvent,
  type FoupOwnershipLedgerOptions,
  type FoupOwnershipLedgerState,
} from './foupOwnershipLedger';

export type ReplayActorIndexes = {
  byOhtId: Record<string, VisualizationEvent[]>;
  byFoupId: Record<string, VisualizationEvent[]>;
  byPortId: Record<string, VisualizationEvent[]>;
  byStationId: Record<string, VisualizationEvent[]>;
  byJunctionId: Record<string, VisualizationEvent[]>;
  byOperationId: Record<string, VisualizationEvent[]>;
  byRequestId: Record<string, VisualizationEvent[]>;
  bySegmentId: Record<string, VisualizationEvent[]>;
};

export type ReplayCheckpoint = {
  /** Exclusive: events with sequence < appliedThroughSequence are applied. */
  appliedThroughSequence: number;
  /** Simulation time of the checkpoint (last applied event.t, or 0). */
  simTime: number;
  /** Event index in `events` of the last applied event, or -1. */
  eventIndex: number;
  ledger: FoupOwnershipLedgerState;
};

export type ReplayPlan = {
  version: number;
  /** Events in ascending sequence order (source order preserved). */
  events: VisualizationEvent[];
  indexes: ReplayActorIndexes;
  checkpoints: ReplayCheckpoint[];
  durationS: number;
};

export type BuildReplayPlanOptions = {
  version?: number;
  durationS?: number;
  /** Build a checkpoint every N applied events (default 32). */
  checkpointEvery?: number;
  ledgerOptions?: FoupOwnershipLedgerOptions;
};

function pushIndex(
  index: Record<string, VisualizationEvent[]>,
  key: string | undefined,
  event: VisualizationEvent
): void {
  if (!key) return;
  (index[key] ??= []).push(event);
}

/**
 * Build an immutable replay plan indexed by actor IDs.
 * Caller must pass already-validated events in ascending sequence order.
 */
export function buildReplayPlan(
  events: readonly VisualizationEvent[],
  options: BuildReplayPlanOptions = {}
): ReplayPlan {
  const byOhtId: Record<string, VisualizationEvent[]> = {};
  const byFoupId: Record<string, VisualizationEvent[]> = {};
  const byPortId: Record<string, VisualizationEvent[]> = {};
  const byStationId: Record<string, VisualizationEvent[]> = {};
  const byJunctionId: Record<string, VisualizationEvent[]> = {};
  const byOperationId: Record<string, VisualizationEvent[]> = {};
  const byRequestId: Record<string, VisualizationEvent[]> = {};
  const bySegmentId: Record<string, VisualizationEvent[]> = {};

  let maxT = 0;
  for (const event of events) {
    pushIndex(byOhtId, event.ohtId, event);
    pushIndex(byFoupId, event.foupId, event);
    pushIndex(byPortId, event.portId, event);
    pushIndex(byPortId, event.fromPortId, event);
    pushIndex(byPortId, event.toPortId, event);
    pushIndex(byStationId, event.stationId, event);
    pushIndex(byJunctionId, event.junctionId, event);
    pushIndex(byJunctionId, event.connectorId, event);
    pushIndex(byOperationId, event.operationId, event);
    pushIndex(byRequestId, event.requestId, event);
    pushIndex(bySegmentId, event.segmentId, event);
    pushIndex(bySegmentId, event.fromSegmentId, event);
    pushIndex(bySegmentId, event.toSegmentId, event);
    maxT = Math.max(maxT, event.t, event.endT ?? event.t);
  }

  const checkpointEvery = Math.max(1, options.checkpointEvery ?? 32);
  const inferredPortInventory = seedPortInventoryFromFirstEvent(
    events,
    options.ledgerOptions?.initialPortInventory ?? {}
  );
  const inferredOwnership = [...(options.ledgerOptions?.initialOwnership ?? [])];
  const seededFoups = new Set(inferredOwnership.map((record) => record.foupId));
  // Earliest DROP per FOUP, ordered lexicographically by (t, sequence). Asking
  // `events.some(...)` per PICKUP made seeding O(events^2), which a live stream
  // pays on every flush against a monotonically growing event log.
  const earliestDropByFoup = new Map<string, { t: number; sequence: number }>();
  for (const event of events) {
    if (event.kind !== 'OHT_DROP' || !event.foupId) continue;
    const current = earliestDropByFoup.get(event.foupId);
    if (
      !current ||
      event.t < current.t ||
      (event.t === current.t && event.sequence < current.sequence)
    ) {
      earliestDropByFoup.set(event.foupId, { t: event.t, sequence: event.sequence });
    }
  }
  for (const event of events) {
    if (
      event.kind === 'OHT_PICKUP' &&
      event.foupId &&
      event.portId &&
      !seededFoups.has(event.foupId)
    ) {
      const firstDrop = earliestDropByFoup.get(event.foupId);
      const deliveredFirst =
        firstDrop !== undefined &&
        (firstDrop.t < event.t ||
          (firstDrop.t === event.t && firstDrop.sequence < event.sequence));
      // Station-owned ports (stocker / tool) stay empty until a DROP commit.
      if (!deliveredFirst && !event.stationId) {
        inferredOwnership.push({
          foupId: event.foupId,
          ownerKind: 'port',
          ownerId: event.portId,
          portId: event.portId,
          stationId: event.stationId,
          sinceSequence: Number.NEGATIVE_INFINITY,
          sinceT: 0,
        });
        seededFoups.add(event.foupId);
      }
    }
  }
  const ledger = new FoupOwnershipLedger({
    initialPortInventory: inferredPortInventory,
    initialOwnership: inferredOwnership,
  });
  const checkpoints: ReplayCheckpoint[] = [
    {
      appliedThroughSequence: Number.NEGATIVE_INFINITY,
      simTime: 0,
      eventIndex: -1,
      ledger: ledger.getState(),
    },
  ];

  events.forEach((event, eventIndex) => {
    ledger.apply(event);
    if ((eventIndex + 1) % checkpointEvery === 0 || eventIndex === events.length - 1) {
      checkpoints.push({
        appliedThroughSequence: event.sequence,
        simTime: event.t,
        eventIndex,
        ledger: ledger.getState(),
      });
    }
  });

  return {
    version: options.version ?? 1,
    events: events.slice(),
    indexes: {
      byOhtId,
      byFoupId,
      byPortId,
      byStationId,
      byJunctionId,
      byOperationId,
      byRequestId,
      bySegmentId,
    },
    checkpoints,
    durationS: options.durationS ?? maxT,
  };
}

/** Binary search: first index with event.sequence > sequence. */
export function firstEventIndexAfterSequence(
  events: readonly VisualizationEvent[],
  sequence: number
): number {
  let lo = 0;
  let hi = events.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (events[mid]!.sequence <= sequence) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Choose the latest checkpoint at or before simTime (by applied event time),
 * preferring the highest appliedThroughSequence among ties.
 */
export function findCheckpointForTime(
  plan: ReplayPlan,
  simTime: number
): ReplayCheckpoint {
  let best = plan.checkpoints[0]!;
  for (const checkpoint of plan.checkpoints) {
    if (checkpoint.simTime <= simTime && checkpoint.eventIndex >= best.eventIndex) {
      best = checkpoint;
    }
  }
  return best;
}
